import { useCreateFieldGetter, useIsSectionVisible } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';
import { useHeaderVisibility } from '@/composables/useHeaderVisibility';

/**
 * Composable for Article Header component customisation
 * Returns header container background styling
 */
export const useArticleHeaderCustomisation = () => {
  const { getField, isVisible: customisationIsVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE,
    sectionKey: PUI.ARTICLES_SECTION_KEYS.HEADER_BACKGROUND,
    blockKey: PUI.ARTICLES_BLOCK_KEYS.HEADER,
  });

  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);

  // uiConfig.header.show takes highest priority over brand customisation (mirrors pl-chat-sdk behaviour)
  const isVisible = useHeaderVisibility(customisationIsVisible);

  return {
    isVisible,
    backgroundColor,
  };
};

/**
 * Composable for Article Header Title customisation
 */
export const useArticleHeaderTitleCustomisation = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE,
    sectionKey: PUI.ARTICLES_SECTION_KEYS.HEADER_TITLE,
    blockKey: PUI.ARTICLES_BLOCK_KEYS.HEADER,
  });

  const textContent = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    isVisible,
    textContent,
    textColor,
  };
};

/**
 * Generic composable for Nav Icon customisation
 * @param sectionKey - The section key for the nav icon (NAV_ICON_1 or NAV_ICON_2)
 */
const useArticleNavIconCustomisation = (sectionKey: string) => {
  const { getField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE,
    sectionKey,
    blockKey: PUI.ARTICLES_BLOCK_KEYS.HEADER,
  });

  const actionColor = getField(PUI.COMMON_FIELD_KEYS.ACTION_COLOR);

  return {
    isVisible,
    actionColor,
  };
};

/**
 * Composable for Nav Icon 1 customisation
 */
export const useArticleNavIcon1Customisation = () => {
  return useArticleNavIconCustomisation(PUI.ARTICLES_SECTION_KEYS.NAV_ICON_1);
};

/**
 * Composable for Nav Icon 2 customisation
 */
export const useArticleNavIcon2Customisation = () => {
  return useArticleNavIconCustomisation(PUI.ARTICLES_SECTION_KEYS.NAV_ICON_2);
};

/**
 * Composable for Article Header Search customisation
 */
export const useArticleHeaderSearchCustomisation = () => {
  const isVisible = useIsSectionVisible({
    registryId: PUI.REGISTRY_IDS.ARTICLE,
    sectionKey: PUI.ARTICLES_SECTION_KEYS.SEARCH_INPUT,
  });

  const { getField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE,
    sectionKey: PUI.ARTICLES_SECTION_KEYS.HEADER_SEARCH,
    blockKey: PUI.ARTICLES_BLOCK_KEYS.HEADER,
  });

  const placeholderText = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);

  return {
    isVisible,
    placeholderText,
  };
};
